import { Input } from './ui/input';
import { Label } from './ui/label';
import type { Recurrence, SubmissionInput } from '~/lib/types';
import { AREAS, AREA_LABELS, CATEGORIES, CATEGORY_LABELS } from '~/lib/labels';
import { countWords } from '~/lib/event-utils';

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const NTH_LABELS: Record<number, string> = {
  1: 'First',
  2: 'Second',
  3: 'Third',
  4: 'Fourth',
  5: 'Last',
};

/**
 * The one event form — used by /submit (per draft card), /e/:token, and the
 * admin add/edit flows. Fully controlled; never loses data.
 */
export function EventForm({
  value,
  onChange,
  idPrefix,
  showRecurrence = true,
}: {
  value: SubmissionInput;
  onChange: (next: SubmissionInput) => void;
  idPrefix: string;
  showRecurrence?: boolean;
}) {
  const set = <K extends keyof SubmissionInput>(key: K, v: SubmissionInput[K]) =>
    onChange({ ...value, [key]: v });

  const words = countWords(value.description);
  const over = words > 75;
  const rec = value.recurrence;

  const setRec = (patch: Partial<Recurrence>) =>
    onChange({
      ...value,
      recurrence: { pattern: 'weekly', dayOfWeek: 3, ...rec, ...patch },
    });

  return (
    <div className="form-grid">
      <div className="field span-2">
        <Label htmlFor={`${idPrefix}-title`}>Event title *</Label>
        <Input
          id={`${idPrefix}-title`}
          value={value.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder="First Friday Networking Breakfast"
          required
        />
      </div>

      <div className="field">
        <Label htmlFor={`${idPrefix}-date`}>Date *</Label>
        <Input
          id={`${idPrefix}-date`}
          type="date"
          value={value.date}
          onChange={(e) => set('date', e.target.value)}
          required
        />
      </div>

      <div className="field-pair">
        <div className="field">
          <Label htmlFor={`${idPrefix}-start`}>Starts *</Label>
          <Input
            id={`${idPrefix}-start`}
            type="time"
            value={value.startTime}
            onChange={(e) => set('startTime', e.target.value)}
            required
          />
        </div>
        <div className="field">
          <Label htmlFor={`${idPrefix}-end`}>Ends</Label>
          <Input
            id={`${idPrefix}-end`}
            type="time"
            value={value.endTime ?? ''}
            onChange={(e) => set('endTime', e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <Label htmlFor={`${idPrefix}-location`}>Venue name *</Label>
        <Input
          id={`${idPrefix}-location`}
          value={value.locationName}
          onChange={(e) => set('locationName', e.target.value)}
          placeholder="The Vault, downtown St. Pete"
          required
        />
      </div>

      <div className="field">
        <Label htmlFor={`${idPrefix}-address`}>Address</Label>
        <Input
          id={`${idPrefix}-address`}
          value={value.address}
          onChange={(e) => set('address', e.target.value)}
          placeholder="123 Central Ave, St. Petersburg, FL"
          autoComplete="off"
        />
      </div>

      <div className="field">
        <Label htmlFor={`${idPrefix}-area`}>Area *</Label>
        <select
          id={`${idPrefix}-area`}
          className="ui-select"
          value={value.area}
          onChange={(e) => set('area', e.target.value as SubmissionInput['area'])}
          required
        >
          {AREAS.map((a) => (
            <option key={a} value={a}>
              {AREA_LABELS[a]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <Label htmlFor={`${idPrefix}-category`}>Event type *</Label>
        <select
          id={`${idPrefix}-category`}
          className="ui-select"
          value={value.category}
          onChange={(e) => set('category', e.target.value as SubmissionInput['category'])}
          required
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="ui-label">Cost</span>
        <label className="check-row" htmlFor={`${idPrefix}-free`}>
          <input
            id={`${idPrefix}-free`}
            type="checkbox"
            checked={value.isFree}
            onChange={(e) =>
              onChange({
                ...value,
                isFree: e.target.checked,
                cost: e.target.checked ? 'Free' : value.cost === 'Free' ? '' : value.cost,
              })
            }
          />
          This event is free to attend
        </label>
        {!value.isFree && (
          <>
            <Label className="sr-only" htmlFor={`${idPrefix}-cost`}>
              Cost details
            </Label>
            <Input
              id={`${idPrefix}-cost`}
              value={value.cost}
              onChange={(e) => set('cost', e.target.value)}
              placeholder="$20 · members free"
            />
          </>
        )}
      </div>

      <div className="field">
        <Label htmlFor={`${idPrefix}-organizer`}>Organizer name *</Label>
        <Input
          id={`${idPrefix}-organizer`}
          value={value.organizerName}
          onChange={(e) => set('organizerName', e.target.value)}
          placeholder="St. Pete Chamber of Commerce"
          required
        />
      </div>

      <div className="field">
        <Label htmlFor={`${idPrefix}-orgsite`}>Organizer website</Label>
        <Input
          id={`${idPrefix}-orgsite`}
          type="url"
          inputMode="url"
          value={value.organizerWebsite}
          onChange={(e) => set('organizerWebsite', e.target.value)}
          placeholder="https://example.org"
        />
      </div>

      <div className="field">
        <Label htmlFor={`${idPrefix}-register`}>Registration / RSVP link</Label>
        <Input
          id={`${idPrefix}-register`}
          type="url"
          inputMode="url"
          value={value.registerUrl}
          onChange={(e) => set('registerUrl', e.target.value)}
          placeholder="https://eventbrite.com/…"
        />
        <p className="field-hint">
          Where attendees sign up. Eventbrite, Meetup, your site — any link works.
        </p>
      </div>

      <div className="field span-2">
        <div className="row-between">
          <Label htmlFor={`${idPrefix}-description`}>Description *</Label>
          <span className={over ? 'word-count over' : 'word-count'} aria-live="polite">
            {words}/75 words
          </span>
        </div>
        <textarea
          id={`${idPrefix}-description`}
          className="ui-textarea"
          value={value.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Who it's for, what happens, why show up. Keep it tight — 75 words max."
          rows={4}
          required
        />
        {over && (
          <p className="form-error" role="alert">
            {words - 75} word{words - 75 === 1 ? '' : 's'} over the limit — trim it down to submit.
          </p>
        )}
      </div>

      {showRecurrence && (
        <div className="field span-2">
          <label className="check-row" htmlFor={`${idPrefix}-repeats`}>
            <input
              id={`${idPrefix}-repeats`}
              type="checkbox"
              checked={!!rec}
              onChange={(e) =>
                e.target.checked ? setRec({}) : onChange({ ...value, recurrence: undefined })
              }
            />
            This event repeats
          </label>

          {rec && (
            <div className="form-grid recurrence-grid">
              <div className="field">
                <Label htmlFor={`${idPrefix}-pattern`}>How often</Label>
                <select
                  id={`${idPrefix}-pattern`}
                  className="ui-select"
                  value={rec.pattern}
                  onChange={(e) => setRec({ pattern: e.target.value as Recurrence['pattern'] })}
                >
                  <option value="weekly">Every week</option>
                  <option value="biweekly">Every other week</option>
                  <option value="monthly-nth-weekday">Monthly (e.g. 2nd Tuesday)</option>
                </select>
              </div>

              <div className="field">
                <Label htmlFor={`${idPrefix}-dow`}>Day of week</Label>
                <select
                  id={`${idPrefix}-dow`}
                  className="ui-select"
                  value={rec.dayOfWeek}
                  onChange={(e) => setRec({ dayOfWeek: Number(e.target.value) })}
                >
                  {WEEKDAY_NAMES.map((name, i) => (
                    <option key={name} value={i}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              {rec.pattern === 'monthly-nth-weekday' && (
                <div className="field">
                  <Label htmlFor={`${idPrefix}-nth`}>Which one</Label>
                  <select
                    id={`${idPrefix}-nth`}
                    className="ui-select"
                    value={rec.nth ?? 1}
                    onChange={(e) => setRec({ nth: Number(e.target.value) })}
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>
                        {NTH_LABELS[n]} {WEEKDAY_NAMES[rec.dayOfWeek]} of the month
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="field">
                <Label htmlFor={`${idPrefix}-until`}>Repeats until (optional)</Label>
                <Input
                  id={`${idPrefix}-until`}
                  type="date"
                  value={rec.until ?? ''}
                  onChange={(e) => setRec({ until: e.target.value || undefined })}
                />
                <p className="field-hint">
                  Leave blank and we'll keep it on the calendar ~3 months out.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Client-side validation — mirrors the API's requirements. */
export function validateSubmission(s: SubmissionInput): string[] {
  const errors: string[] = [];
  if (!s.title.trim()) errors.push('Event title is required.');
  if (!s.date) errors.push('Date is required.');
  if (!s.startTime) errors.push('Start time is required.');
  if (!s.locationName.trim()) errors.push('Venue name is required.');
  if (!s.organizerName.trim()) errors.push('Organizer name is required.');
  if (!s.description.trim()) errors.push('Description is required.');
  if (countWords(s.description) > 75) errors.push('Description is over 75 words.');
  return errors;
}
